import React from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import MaskedInput from 'react-text-mask';

import PropTypes from 'prop-types';

import classes from './style.module.scss';
import Radio from '@/components/common/elements/Radio';
import AddressIcon from '@/components/common/icons/address.svg';
import folderImage from '@/components/common/icons/folder-image.svg';
import ResponsibleIcon from '@/components/common/icons/responsible.svg';
import walletImage from '@/components/common/icons/wallet2.svg';
import { USERNAME_MASK } from '@/helpers/mask';

const classificationsNamesTranslations = {
  'CLASSIFICATION.IMOBILIARIA': 'Imobiliária',
  'CLASSIFICATION.CORRETOR_DE_IMOVEIS': 'Corretor de imóveis',
  'CLASSIFICATION.PROPRIETARIO': 'Proprietário',
  'CLASSIFICATION.CONDOMINIO': 'Condomínio',
  'CLASSIFICATION.ADMINISTRADORA_DE_CONDOMINIOS': 'Administradora de condomínios',
  'CLASSIFICATION.SINDICO_PROFISSIONAL': 'Síndico profissional',
  'CLASSIFICATION.CONSTRUTORA_INCORPORADORA': 'Construtora, Incorporadora',
  'CLASSIFICATION.EMPREITEIRA': 'Empreiteira',
  'CLASSIFICATION.INSTITUICAO_FINANCEIRA': 'Instituição Financeira',
  'CLASSIFICATION.REDE_DE_VAREJO': 'Rede de Varejo',
  'CLASSIFICATION.PESSOA_FISICA': 'Pessoa física',
  'CLASSIFICATION.OUTROS': 'Outros',
};

const ClientFormLinkComponent = ({
  busy,
  client,
  classifications,
  states,
  cities,
  neighborhoods,
  type,
  privacyPolicy,
  receiveInformation,
  onChange,
  onSubmit,
  zipcodeMask,
  cnpjMask,
  cpfOnlyMask,
  phoneMask,
  cellphoneMask,
  cellphoneMaskLength,
  documentError,
  useTextInputForAddress = false,
  country = 'BR',
}) => {
  const intl = useIntl();

  // Helper function to render error message
  const renderError = (error) => {
    if (error) {
      return <div className={classes.errorMessage}>{error}</div>;
    }
    return null;
  };

  return (
    <div className={classes.clientFormLink}>
      <div className={classes.formContainer}>
        <form onSubmit={onSubmit}>
          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={folderImage} alt={intl.formatMessage({ id: 'Registration_data' })} />
            </div>
            {intl.formatMessage({ id: 'Registration_data' })}
          </h3>

          <div className={`${classes.field} ${classes.hasRadio}`}>
            <div className={classes.radio}>
              <Radio
                name="client_type"
                className={classes.radioInput}
                id="clientTypePJ"
                data-cy="clientTypePJ"
                value="pj"
                onChange={onChange}
                checked={type === 'pj'}
              />

              <label htmlFor="clientTypePJ">{intl.formatMessage({ id: 'LEGAL_PERSON' })}</label>
            </div>

            <div className={classes.radio}>
              <Radio
                name="client_type"
                className={classes.radioInput}
                id="clientTypePF"
                data-cy="clientTypePF"
                value="pf"
                onChange={onChange}
                checked={type === 'pf'}
              />

              <label htmlFor="clientTypePF">{intl.formatMessage({ id: 'NATURAL_PERSON' })}</label>
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            {type === 'pj' ? (
              <>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="name">{intl.formatMessage({ id: 'FANTASY_NAME' })}*</label>

                  <input
                    type="text"
                    name="name"
                    id="name"
                    data-cy="name"
                    required
                    value={client?.name || ''}
                    onChange={onChange}
                  />
                </div>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="company_name">{intl.formatMessage({ id: 'COMPANY_NAME' })}*</label>

                  <input
                    type="text"
                    name="company_name"
                    id="company_name"
                    data-cy="company_name"
                    required
                    value={client?.company_name || ''}
                    onChange={onChange}
                  />
                </div>
              </>
            ) : (
              <div className={`${classes.field} ${classes.is12}`}>
                <label htmlFor="name">{intl.formatMessage({ id: 'NAME' })}*</label>

                <input
                  type="text"
                  name="name"
                  id="name"
                  data-cy="name"
                  required
                  value={client?.name || ''}
                  onChange={onChange}
                />
              </div>
            )}
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="classification">{intl.formatMessage({ id: 'CLASSIFICATION' })}*</label>

              <Select
                name="classification"
                id="classification"
                data-cy="classification"
                className="select-container"
                classNamePrefix="select"
                placeholder=""
                value={classifications.find(classification => classification === client?.classification) ? { 
                  label: classificationsNamesTranslations[`CLASSIFICATION.${client.classification}`], 
                  value: client.classification 
                } : null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      name: 'classification',
                      value: selectedOption ? selectedOption.value : ''
                    }
                  };
                  onChange(event);
                }}
                options={classifications.map(classification => ({
                  label: classificationsNamesTranslations[`CLASSIFICATION.${classification}`],
                  value: classification
                }))}
                isClearable
                noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                required
              />
            </div>

            {type === 'pj' ? (
              <>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="cnpj">{intl.formatMessage({ id: 'CNPJ' })}*</label>

                  <MaskedInput
                    type="text"
                    name="document"
                    id="cnpj"
                    data-cy="cnpj"
                    required
                    value={client?.document || ''}
                    onChange={onChange}
                    guide={false}
                    mask={cnpjMask}
                  />
                  {renderError(documentError)}
                </div>
              </>
            ) : (
              <div className={`${classes.field} ${classes.is6}`}>
                <label htmlFor="cpf">{intl.formatMessage({ id: 'CPF' })}*</label>

                <MaskedInput
                  type="text"
                  name="document"
                  id="cpf"
                  data-cy="cpf"
                  required
                  value={client?.document || ''}
                  onChange={onChange}
                  guide={false}
                  mask={cpfOnlyMask}
                />
                {renderError(documentError)}
              </div>
            )}
          </div>
          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="username">{intl.formatMessage({ id: 'USERNAME' })}*</label>

              <MaskedInput
                type="text"
                name="username"
                id="username"
                data-cy="username"
                required
                value={client?.username || ''}
                onChange={onChange}
                guide={false}
                mask={USERNAME_MASK}
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="email">{intl.formatMessage({ id: 'EMAIL' })}*</label>

              <input
                type="email"
                name="contact[email]"
                id="email"
                data-cy="email"
                required
                value={client?.contact?.email || ''}
                onChange={onChange}
              />
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="phone">{intl.formatMessage({ id: 'PHONE' })}</label>

              <MaskedInput
                type="text"
                name="contact[phone]"
                id="phone"
                data-cy="phone"
                value={client?.contact?.phone || ''}
                onChange={onChange}
                guide={false}
                mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="cellphone">
                {intl.formatMessage({ id: 'CELLPHONE' })}
                {type === 'pj' ? '*' : ''}
              </label>

              <MaskedInput
                type="text"
                name="contact[cellphone]"
                id="cellphone"
                data-cy="cellphone"
                value={client?.contact?.cellphone || ''}
                onChange={onChange}
                guide={false}
                mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                required={type === 'pj'}
              />
            </div>
          </div>

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={AddressIcon} alt={intl.formatMessage({ id: 'ADDRESS' })} />
            </div>
            {intl.formatMessage({ id: 'ADDRESS' })}
          </h3>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="zipcode">{intl.formatMessage({ id: 'CEP' })}*</label>

              <MaskedInput
                type="text"
                name="address[zipcode]"
                id="zipcode"
                data-cy="zipcode"
                value={client?.address?.zipcode || ''}
                onChange={onChange}
                guide={false}
                mask={zipcodeMask}
                required
              />
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is2}`}>
              <label htmlFor="state">{intl.formatMessage({ id: 'STATE' })}*</label>

              {useTextInputForAddress ? (
                <input
                  type="text"
                  name="address[state]"
                  id="state"
                  data-cy="state"
                  value={client?.address?.state || ''}
                  onChange={onChange}
                  required
                />
              ) : (
                <Select
                  name="address[state_id]"
                  id="state"
                  data-cy="state"
                  className="select-container"
                  classNamePrefix="select"
                  placeholder=""
                  value={(() => {
                    if (!client?.address?.state_id) return null;
                    const foundState = states.find(state => state.id === client.address.state_id);
                    return foundState ? { label: foundState.name, value: foundState.id, id: foundState.id } : null;
                  })()}
                  onChange={(selectedOption) => {
                    const event = {
                      target: {
                        name: 'address[state_id]',
                        value: selectedOption ? selectedOption.id : ''
                      }
                    };
                    onChange(event);
                  }}
                  options={states.map(state => ({ label: state.name, value: state.id, id: state.id }))}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                  required
                />
              )}
            </div>

            <div className={`${classes.field} ${classes.is5}`}>
              <label htmlFor="city">{intl.formatMessage({ id: 'CITY' })}*</label>

              {useTextInputForAddress ? (
                <input
                  type="text"
                  name="address[city]"
                  id="city"
                  data-cy="city"
                  value={client?.address?.city || ''}
                  onChange={onChange}
                  required
                />
              ) : (
                <CreatableSelect
                  name="address[city]"
                  id="city"
                  data-cy="city"
                  className="select-container"
                  classNamePrefix="select"
                  placeholder=""
                  value={(() => {
                    if (!client?.address?.city) return null;
                    return { label: client.address.city, value: client.address.city };
                  })()}
                  onChange={(selectedOption) => {
                    const event = {
                      target: {
                        name: 'address[city]',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    };
                    onChange(event);
                  }}
                  options={cities.map(city => ({ label: city.name, value: city.name }))}
                  formatCreateLabel={(inputValue) => `${intl.formatMessage({ id: 'ADD' })} "${inputValue}"`}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                  isValidNewOption={(inputValue) => inputValue && inputValue.trim().length > 0}
                  createOptionPosition="first"
                  required
                />
              )}
            </div>

            <div className={`${classes.field} ${classes.is5}`}>
              <label htmlFor="neighborhood">{intl.formatMessage({ id: 'NEIGHBORHOOD' })}*</label>

              {useTextInputForAddress ? (
                <input
                  type="text"
                  name="address[neighborhood]"
                  id="neighborhood"
                  data-cy="neighborhood"
                  value={client?.address?.neighborhood || ''}
                  onChange={onChange}
                  required
                />
              ) : (
                <CreatableSelect
                  name="address[neighborhood]"
                  id="neighborhood"
                  data-cy="neighborhood"
                  className="select-container"
                  classNamePrefix="select"
                  placeholder=""
                  value={(() => {
                    if (!client?.address?.neighborhood) return null;
                    return { label: client.address.neighborhood, value: client.address.neighborhood };
                  })()}
                  onChange={(selectedOption) => {
                    const event = {
                      target: {
                        name: 'address[neighborhood]',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    };
                    onChange(event);
                  }}
                  options={neighborhoods.map(neighborhood => ({ label: neighborhood.name, value: neighborhood.name }))}
                  formatCreateLabel={(inputValue) => `${intl.formatMessage({ id: 'ADD' })} "${inputValue}"`}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                  isValidNewOption={(inputValue) => inputValue && inputValue.trim().length > 0}
                  createOptionPosition="first"
                  required
                />
              )}
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is8}`}>
              <label htmlFor="street">{intl.formatMessage({ id: 'STREET' })}*</label>

              <input
                type="text"
                name="address[street]"
                id="street"
                data-cy="street"
                value={client?.address?.street || ''}
                onChange={onChange}
                required
              />
            </div>

            <div className={`${classes.field} ${classes.is4}`}>
              <label htmlFor="number">{intl.formatMessage({ id: 'NUMBER' })}*</label>

              <input
                type="text"
                name="address[number]"
                id="number"
                data-cy="number"
                value={client?.address?.number || ''}
                onChange={onChange}
                required
              />
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is12}`}>
              <label htmlFor="complement">{intl.formatMessage({ id: 'COMPLEMENT' })}</label>

              <input
                type="text"
                name="address[complement]"
                id="complement"
                data-cy="complement"
                value={client?.address?.complement || ''}
                onChange={onChange}
              />
            </div>
          </div>

          {/* Hidden inputs to maintain IDs for backend submission */}
          <input type="hidden" name="address[state_id]" value={client?.address?.state_id || ''} />
          <input type="hidden" name="address[city_id]" value={client?.address?.city_id || ''} />
          <input type="hidden" name="address[neighborhood_id]" value={client?.address?.neighborhood_id || ''} />

          {type === 'pj' && (
            <>
              <h3 className={classes.subtitle}>
                <div className={classes.icon}>
                  <img src={ResponsibleIcon} alt={intl.formatMessage({ id: 'COMMERCIAL_RESPONSIBLE' })} />
                </div>
                {intl.formatMessage({ id: 'COMMERCIAL_RESPONSIBLE' })}
              </h3>

              <div className={classes.field}>
                <label htmlFor="responsibleName">{intl.formatMessage({ id: 'NAME' })}*</label>

                <input
                  type="text"
                  name="responsible[name]"
                  id="responsibleName"
                  data-cy="responsibleName"
                  required
                  value={client?.responsible?.name || ''}
                  onChange={onChange}
                />
              </div>

              <div className={`${classes.field} ${classes.isFlex}`}>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="responsibleDocument">{intl.formatMessage({ id: 'CPF' })}*</label>

                  <MaskedInput
                    type="text"
                    name="responsible[document]"
                    id="responsibleDocument"
                    data-cy="responsibleDocument"
                    required
                    value={client?.responsible?.document || ''}
                    onChange={onChange}
                    guide={false}
                    mask={cpfOnlyMask}
                  />
                </div>

                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="responsiblePhone">{intl.formatMessage({ id: 'PHONE' })}</label>

                  <MaskedInput
                    type="text"
                    name="responsible[phone]"
                    id="responsiblePhone"
                    data-cy="responsiblePhone"
                    value={client?.responsible?.phone || ''}
                    onChange={onChange}
                    guide={false}
                    mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                  />
                </div>
              </div>

              <div className={`${classes.field} ${classes.isFlex}`}>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="responsibleCellphone">{intl.formatMessage({ id: 'CELLPHONE' })}</label>

                  <MaskedInput
                    type="text"
                    name="responsible[cellphone]"
                    id="responsibleCellphone"
                    data-cy="responsibleCellphone"
                    value={client?.responsible?.cellphone || ''}
                    onChange={onChange}
                    guide={false}
                    mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                  />
                </div>

                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="ResponsibleEmail">{intl.formatMessage({ id: 'EMAIL' })}</label>

                  <input
                    type="text"
                    name="responsible[email]"
                    id="ResponsibleEmail"
                    data-cy="ResponsibleEmail"
                    value={client?.responsible?.email || ''}
                    onChange={onChange}
                  />
                </div>
              </div>
            </>
          )}

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={walletImage} alt={intl.formatMessage({ id: 'PROPERTIES_PORTFOLIO' })} />
            </div>
            {intl.formatMessage({ id: 'PROPERTIES_PORTFOLIO' })}
          </h3>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="properties_purchase_sell">
                {intl.formatMessage({ id: 'PROPERTIES_FOR_PURCHASE_SELL' })}
              </label>

              <input
                type="number"
                name="propertiesWallet[properties_purchase_sell]"
                id="properties_purchase_sell"
                data-cy="properties_purchase_sell"
                min="0"
                value={client?.propertiesWallet?.properties_purchase_sell || ''}
                onChange={onChange}
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="properties_rent">{intl.formatMessage({ id: 'PROPERTIES_FOR_RENT' })}</label>

              <input
                type="number"
                name="propertiesWallet[properties_rent]"
                id="properties_rent"
                data-cy="properties_rent"
                min="0"
                value={client?.propertiesWallet?.properties_rent || ''}
                onChange={onChange}
              />
            </div>
          </div>
          <div className={classes.field}>
            <label htmlFor="potential_inspections">{intl.formatMessage({ id: 'MONTHLY_POTENTIAL_INSPECTIONS' })}</label>

            <input
              type="number"
              name="propertiesWallet[potential_inspections]"
              id="potential_inspections"
              data-cy="potential_inspections"
              min="0"
              value={client?.propertiesWallet?.potential_inspections || ''}
              onChange={onChange}
            />
          </div>

          <div className={classes.field}>
            <div className={classes.checkbox}>
              <label>
                <input
                  required
                  type="checkbox"
                  name="privacy_policy"
                  id="privacy_policy"
                  data-cy="privacy_policy"
                  onChange={onChange}
                  checked={privacyPolicy}
                />

                <div>
                  {intl.formatMessage({ id: 'CLIENT_LINK.PRIVACY_POLICY_AGREEMENT' })}{' '}
                  <a
                    href={
                      country === 'PT'
                        ? 'https://s3.amazonaws.com/contrato.redevistorias.com.br/72BFCE5E-9A40-43AB-8D28-F610B8E1642E/0203BB16-7E30-4613-97A6-001235A0C844.pdf'
                        : 'https://s3.amazonaws.com/contrato.redevistorias.com.br/E11C7828-481F-4238-843B-DFFB2CA65E2D/CBDAB8FD-07BD-4F42-84D1-99DBF225AC15.pdf'
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {intl.formatMessage({ id: 'CLIENT_LINK.PRIVACY_POLICY' })}
                  </a>
                </div>
              </label>
            </div>
          </div>

          <div className={classes.field}>
            <div className={classes.checkbox}>
              <label>
                <input
                  type="checkbox"
                  name="receive_information"
                  id="receive_information"
                  data-cy="receive_information"
                  onChange={onChange}
                  checked={receiveInformation}
                />
                {intl.formatMessage({ id: 'CLIENT_LINK.RECEIVE_INFORMATION_AGREEMENT' })}
              </label>
            </div>
          </div>

          <div className={classes.field}>
            <p className={classes.text}>{intl.formatMessage({ id: 'CLIENT_LINK.TERMS_CONFIRMATION' })}</p>
          </div>

          <div className={classes.cardFooter}>
            <button type="submit" className={`${classes.button} ${classes.isGreen}`} disabled={busy}>
              {intl.formatMessage({ id: 'CLIENT_LINK.FINALIZE_REGISTRATION' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ClientFormLinkComponent.propTypes = {
  busy: PropTypes.bool.isRequired,
  client: PropTypes.object,
  classifications: PropTypes.array.isRequired,
  states: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  neighborhoods: PropTypes.array,
  type: PropTypes.string,
  privacyPolicy: PropTypes.bool,
  receiveInformation: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  zipcodeMask: PropTypes.array.isRequired,
  cnpjMask: PropTypes.array.isRequired,
  cpfOnlyMask: PropTypes.array.isRequired,
  phoneMask: PropTypes.array.isRequired,
  cellphoneMask: PropTypes.array.isRequired,
  cellphoneMaskLength: PropTypes.number.isRequired,
  documentError: PropTypes.string,
  useTextInputForAddress: PropTypes.bool,
  country: PropTypes.string,
};

ClientFormLinkComponent.defaultProps = {
  client: null,
  type: null,
  neighborhoods: [],
  documentError: '',
  useTextInputForAddress: false,
  country: 'BR',
};

export default ClientFormLinkComponent;
